import React, { Component, ErrorInfo, ReactNode } from "react";
import newRelicErrorReport from "src/utils/newRelic/newRelicErrorReport";
import { USER_INFO_STORAGE_KEY } from "src/utils/RoleBasedSecurity/Session";
import PageExternalScriptError from "src/utils/ErrorPages/PageExternalScriptError/PageExternalScriptError";
import { generateCorrelationId, formatErrorTimestamp } from "./utils";

interface Props {
  children: ReactNode;
  module: string;
}

interface State {
  hasError: boolean;
  correlationId: string;
  errorDate: string;
}

class HRToolsErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, correlationId: "", errorDate: "" };
  }

  static getDerivedStateFromError(): Pick<State, "hasError"> {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    const correlationId = generateCorrelationId();
    const errorDate = formatErrorTimestamp(new Date());

    this.setState({ correlationId, errorDate });

    let companyId = "";
    let userId = "";
    let role = "";
    try {
      const raw =
        sessionStorage.getItem(USER_INFO_STORAGE_KEY) ||
        localStorage.getItem(USER_INFO_STORAGE_KEY);
      if (raw) {
        const userInfo = JSON.parse(raw);
        companyId = userInfo.companyId || "";
        userId = userInfo.id || "";
        role = userInfo.role || "";
      }
    } catch {
      // user info unavailable
    }

    Object.entries({
      correlationId,
      errorDate,
      errorType: "Application",
      module: this.props.module,
      companyId,
      userId,
      role,
    }).forEach(([key, value]) => {
      window.newrelic?.setCustomAttribute(key, value);
    });

    newRelicErrorReport(
      error,
      `${this.props.module.toUpperCase().replace(/\s+/g, "_")} ERROR BOUNDARY [${correlationId}]-stack:${errorInfo.componentStack}`
    );
  }

  render() {
    if (this.state.hasError) {
      return (
        <PageExternalScriptError
          errorData={{
            correlationId: this.state.correlationId,
            date: this.state.errorDate,
            type: "Application",
          }}
        />
      );
    }

    return this.props.children;
  }
}

export default HRToolsErrorBoundary;
